import { Fragment, useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { Card, CardHeader } from '../../components/ui/Card';
import { Table, THead, Th, Td, Nil, TableFooter, isConstant } from '../../components/ui/Table';
import { Pagination, usePagination } from '../../components/ui/Pagination';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/States';
import { formatDayMonth } from '../../utils/dateFormat';
import { useCompanyTimeline, useVisitingCompanies } from '../../hooks/useJourneys';
import { fmtNumber } from '../Dashboard/gtm/components/format';
import type { ListPeriod } from '../../api/web-analytics';

/** A column filled for fewer than 1 company in 5 is hidden as mostly empty (13 of 90 had LinkedIn ads on 12 Oct). */
const MOSTLY_EMPTY_SHARE = 0.2;

/** Attio's companies list; there's no documented deep link that pre-fills a search. */
export const ATTIO_COMPANIES_URL = 'https://app.attio.com/sequesto/companies';

function Day({ iso }: { iso: string | null }) {
  return iso ? <>{formatDayMonth(iso)}</> : <Nil />;
}

/**
 * Marketing → Website: the companies RB2B identified on the site, with what
 * else we know about each — in Attio or not, LinkedIn ads seen, last outreach.
 */
export function CompaniesCard({ period, label }: { period: ListPeriod; label?: string }) {
  const { data, isLoading, error, refetch } = useVisitingCompanies(period);
  const [selected, setSelected] = useState<string | null>(null);
  const rows = data?.companies ?? [];
  // Schools and universities nobody put in Attio, left out by the API (optional: older API builds don't send it).
  const nonBuyers = (data as { leftOut?: { nonBuyers?: string[] } } | undefined)?.leftOut?.nonBuyers ?? [];
  const pager = usePagination(rows, 15);
  // LinkedIn ad impressions and outreach are on a handful of companies at most: under
  // 1 in 5, the column is mostly dots, so it goes and those few companies get a note
  // under their name instead.
  const withAds = rows.filter((r) => r.linkedinImpressions > 0).length;
  const showAds = rows.length > 0 && withAds / rows.length >= MOSTLY_EMPTY_SHARE;
  const withOutreach = rows.filter((r) => r.lastOutboundAt).length;
  const showOutreach =
    rows.length > 0 && withOutreach / rows.length >= MOSTLY_EMPTY_SHARE && !isConstant(rows, (r) => r.lastOutboundAt);
  const colCount = 5 + (showOutreach ? 1 : 0) + (showAds ? 1 : 0);

  return (
    <Card>
      <CardHeader
        title="Companies on the site"
        description={`${label ? `${label} · identified` : 'Identified'} by RB2B, most visits first. A visit is one person on one day, as in the timeline. The arrow opens companies already in Attio; click a company for everything that happened with it.`}
      />
      {isLoading ? (
        <LoadingState rows={3} />
      ) : error ? (
        <ErrorState title="Couldn’t load visiting companies" error={error} onRetry={() => refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState
          compact
          title="No companies identified in this period"
          description="They arrive from RB2B's webhook as visits happen."
        />
      ) : (
        <>
          <Table minWidth={820}>
            <THead>
              <tr>
                <Th>Company</Th>
                <Th>Pages viewed</Th>
                <Th align="right">Visits</Th>
                <Th align="right">Last visit</Th>
                {showAds && <Th align="right">LinkedIn ads 90d</Th>}
                {showOutreach && <Th align="right">Last outreach</Th>}
                <Th align="right">Attio</Th>
              </tr>
            </THead>
            <tbody>
              {pager.pageItems.map((r) => (
                <Fragment key={r.domain}>
                  <tr
                    onClick={() => setSelected(selected === r.domain ? null : r.domain)}
                    className={`border-b border-border-light align-top cursor-pointer hover:bg-surface-secondary ${selected === r.domain ? 'bg-surface-secondary' : ''}`}
                  >
                    <Td className="max-w-[18rem]">
                      <div className="break-words text-text-primary">{r.name}</div>
                      {(() => {
                        const sub = [
                          r.domain,
                          r.industry,
                          r.employeeCount ? `${r.employeeCount} employees` : null,
                          !showAds && r.linkedinImpressions > 0
                            ? `${fmtNumber(r.linkedinImpressions)} LinkedIn ad impressions (90d)`
                            : null,
                          !showOutreach && r.lastOutboundAt ? `last outreach ${formatDayMonth(r.lastOutboundAt)}` : null,
                        ]
                          .filter(Boolean)
                          .join(' · ');
                        return (
                          <div className="break-words text-body-xs text-text-tertiary">{sub}</div>
                        );
                      })()}
                    </Td>
                    <Td className="text-body-xs text-text-secondary" title={r.topPages.join(', ')}>
                      {r.topPages.join(', ') || <Nil />}
                    </Td>
                    <Td align="right" className="tabular-nums">
                      {fmtNumber(r.visits)}
                      {r.pageViews !== r.visits && (
                        <div className="whitespace-nowrap text-body-xs text-text-tertiary">
                          {fmtNumber(r.pageViews)} page views
                        </div>
                      )}
                      {r.people > 0 && <div className="text-body-xs text-text-tertiary">{r.people} named</div>}
                    </Td>
                    <Td align="right" className="whitespace-nowrap text-text-secondary">
                      <Day iso={r.lastVisitAt} />
                    </Td>
                    {showAds && (
                      <Td align="right" className="tabular-nums text-text-secondary">
                        {r.linkedinImpressions > 0 ? fmtNumber(r.linkedinImpressions) : <Nil />}
                      </Td>
                    )}
                    {showOutreach && (
                      <Td align="right" className="whitespace-nowrap text-text-secondary">
                        <Day iso={r.lastOutboundAt} />
                      </Td>
                    )}
                    <Td align="right" className="whitespace-nowrap">
                      {r.attioUrl ? (
                        <a
                          href={r.attioUrl}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`Open ${r.name} in Attio`}
                          title="Open in Attio"
                          className="inline-flex h-7 w-7 items-center justify-center rounded text-text-tertiary hover:bg-surface-secondary hover:text-text-primary"
                        >
                          <ArrowUpRight className="h-4 w-4" aria-hidden />
                        </a>
                      ) : null}
                    </Td>
                  </tr>
                  {selected === r.domain && (
                    <tr className="border-b border-border-light">
                      <td colSpan={colCount} className="py-3">
                        {!r.attioUrl && (
                          <p className="mb-2 ml-2 text-body-xs text-text-tertiary">
                            Not in Attio.{' '}
                            <a
                              href={ATTIO_COMPANIES_URL}
                              target="_blank"
                              rel="noreferrer"
                              // Attio has no search-by-URL link, so the domain goes on the clipboard to paste into its search.
                              onClick={() => void navigator.clipboard?.writeText(r.domain).catch(() => undefined)}
                              title={`Opens Attio companies with “${r.domain}” copied, to search for it`}
                              className="text-accent hover:underline"
                            >
                              Search or add it in Attio
                            </a>
                          </p>
                        )}
                        <CompanyTimeline domain={r.domain} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </Table>
          <Pagination page={pager.page} pageCount={pager.pageCount} total={pager.total} pageSize={pager.pageSize} onPage={pager.setPage} />
          <TableFooter>
            {!showAds &&
              `LinkedIn ads 90d column hidden: ${withAds === 0 ? 'no company' : `only ${fmtNumber(withAds)} of ${fmtNumber(rows.length)}`} saw our ads (noted under the name). `}
            {!showOutreach &&
              (withOutreach === 0
                ? 'Last outreach column hidden: no outreach to any of these companies yet (lemlist). '
                : `Last outreach column hidden: ${withOutreach === rows.length ? 'the same day for every company' : `only ${fmtNumber(withOutreach)} of ${fmtNumber(rows.length)} reached out to`} (lemlist, noted under the name). `)}
            {nonBuyers.length > 0 && (
              <span title={nonBuyers.join(', ')}>
                {fmtNumber(nonBuyers.length)} {nonBuyers.length === 1 ? 'school or university' : 'schools and universities'} not in Attio left out, as never buyers ({nonBuyers.slice(0, 3).join(', ')}{nonBuyers.length > 3 ? '…' : ''}).{' '}
              </span>
            )}
            Names are Attio’s when the company is in Attio; its industry and size are RB2B’s only when RB2B matched that same company.
            Visits count one per person per day, the way the timeline groups them; RB2B reports every page view as its
            own row, so a company can show more page views than visits. Size and industry are shown only when RB2B’s company name fits the
            domain; RB2B’s location is left out because it often is the visitor’s network, not the company’s.
          </TableFooter>
        </>
      )}
    </Card>
  );
}

/** Everything that happened with one company, newest first: visits, outreach, ads, emails, meetings. */
function CompanyTimeline({ domain }: { domain: string }) {
  const { data, isLoading, error, refetch } = useCompanyTimeline(domain);
  if (isLoading) return <LoadingState rows={2} />;
  if (error || !data) {
    return <ErrorState compact title="Couldn’t load this company’s timeline" error={error} onRetry={() => refetch()} />;
  }
  if (data.items.length === 0) return <EmptyState compact title="Nothing recorded yet" />;
  return (
    <ul className="space-y-0.5 border-l border-border pl-3 ml-2">
      {data.items.map((t, i) => (
        <li key={`${t.at}-${i}`} className="text-body-sm text-text-secondary">
          <span className="tabular-nums text-text-tertiary mr-2">{formatDayMonth(t.at)}</span>
          {t.who && <span className="text-text-primary">{t.who} · </span>}
          {t.text}
        </li>
      ))}
    </ul>
  );
}
